import { useState } from "react";
import styled from "styled-components";
import { hideAboveMobile, hideOnMobile } from "../lib/mediaQuery";
import { SortOption } from "../utils/sortBy";
import { Flex } from "./Grid";
import { Icon } from "./Icon";
import { Body, Footnote } from "./Typography";

interface Props {
  setSelectedSortBy: (selectedSortBy: SortOption) => void;
  sortOptions: SortOption[];
  selectedSortBy?: SortOption;
}

export const SortBy = ({
  setSelectedSortBy,
  sortOptions,
  selectedSortBy,
}: Props) => {
  const [isOpen, setIsOpen] = useState<boolean>();
  return (
    <Container
      align={"flex-start"}
      direction={"column"}
      onClick={() => setIsOpen(!isOpen)}
      data-is-open={isOpen}
    >
      <Header align={"center"} onClick={() => setIsOpen(!isOpen)}>
        <Body css={hideOnMobile}>Sort: {selectedSortBy.text}</Body>
        <Footnote css={hideAboveMobile}>Sort: {selectedSortBy.text}</Footnote>
        <Chevron />
      </Header>
      <Menu>
        {sortOptions.map((option) => (
          <Option
            onClick={() => {
              setSelectedSortBy(option);
              setIsOpen(false);
            }}
            data-is-selected={option === selectedSortBy}
          >
            <Body css={hideOnMobile}>{option.text}</Body>
            <Footnote css={hideAboveMobile}>{option.text}</Footnote>
          </Option>
        ))}
      </Menu>
    </Container>
  );
};

const Container = styled(Flex)`
  position: relative;
  cursor: pointer;
`;

const Header = styled(Flex)`
  margin-bottom: var(--spacing-xxs);
`;

const Chevron = styled(Icon).attrs({ name: "ChevronDown" })`
  margin-left: var(--spacing-xxs);
  transition: rotate var(--transition-speed-slow) var(--easing-standard);
  rotate: 0deg;
  [data-is-open="true"] & {
    rotate: 180deg;
  }
`;

const Menu = styled.ul`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: var(--depth-highest);
  min-width: 200px;
  opacity: 0;
  flex-direction: column;
  justify-content: flex-start;
  list-style: none;
  padding: 0;
  height: 0;
  transition-duration: var(--transition-speed-slow);
  transition-delay: 150ms;
  transition-property: opacity;
  [data-is-open="true"] & {
    will-change: backdrop-filter;
    backdrop-filter: blur(25px);
    background-color: rgba(0, 0, 0, 15%);
    overflow: hidden;
    border-radius: var(--spacing-xs);
    border: 1px solid rgba(var(--black-light), 65%);
    transition-duration: var(--transition-speed-normal);
    transition-property: opacity;
    opacity: 1;
    height: fit-content;
  }
  [data-is-open="false"] & {
    //because Safari has no respect
    backdrop-filter: blur(0px);
    background-color: rgba(0, 0, 0, 0%);
    overflow: hidden;
    border-radius: var(--spacing-xs);
    border: 1px solid rgba(var(--black-light), 0%);
    transition-duration: var(--transition-speed-normal);
    transition-property: opacity;
    opacity: 0;
    height: 0;
  }
`;

const Option = styled.li`
  padding: var(--spacing-xs) 12px;
  display: none;
  :hover {
    background: rgba(255, 255, 255, 0.15);
  }
  [data-is-open="true"] & {
    display: block;
  }
  &[data-is-selected="true"] {
    background: rgba(var(--cyan-dark));
  }
`;
